import { useEffect, useState, type FormEvent } from "react";
import { Api, ApiError, type Me, type Role, type TeamInfo } from "../api.js";
import { errorText } from "../auth/Screens.js";
import { useI18n } from "../i18n.js";
import { Badge, Field, TextInput } from "./ui.js";
import { UpgradeNote } from "./UpgradeNote.js";

const ROLES: Role[] = ["owner", "manager", "member"];

export function Team({ api, me, accountName, onLeft }: { api: Api; me: Me; accountName: string; onLeft: () => void }) {
  const { t, lang } = useI18n();
  const [team, setTeam] = useState<TeamInfo | null>(null);
  const [error, setError] = useState<ApiError | string | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("member");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<string | null>(null);

  const load = () => api.team().then(setTeam, (e) => setError(e instanceof ApiError ? e : t("common.network")));
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, me.activeAccountId]);

  const fail = (e: unknown) => setError(e instanceof ApiError ? e : t("common.network"));
  const canManage = team ? team.yourRole !== "member" : false;
  const isOwner = team?.yourRole === "owner";
  const roleOptions = isOwner ? ROLES : ROLES.filter((r) => r !== "owner");

  const send = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSent(null);
    try {
      await api.invite(email.trim(), role);
      setSent(email.trim());
      setEmail("");
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const doRole = async (userId: string, next: Role) => {
    setError(null);
    try {
      await api.setRole(userId, next);
      await load();
    } catch (err) {
      fail(err);
      await load();
    }
  };

  const remove = async (userId: string, name: string, you: boolean) => {
    if (!window.confirm(you ? t("team.leaveConfirm", { account: accountName }) : t("team.removeConfirm", { name }))) return;
    setError(null);
    try {
      await api.removeMember(userId);
      if (you) onLeft();
      else await load();
    } catch (err) {
      fail(err);
    }
  };

  const errorBox = error && (typeof error === "string" ? <div className="banner warn" role="alert">{error}</div> : error.status === 402 ? <UpgradeNote error={error} /> : <div className="banner warn" role="alert">{error.code === "last_owner" ? t("team.lastOwner") : error.message}</div>);

  return (
    <main className="page" id="main">
      <h1>{t("team.title")}</h1>
      <p>{t("team.lead")}</p>
      {errorBox}
      {!team && !error && <p>{t("common.loading")}</p>}
      {team && (
        <>
          <h2>{t("team.members")}</h2>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("common.name")}</th>
                  <th>{t("common.email")}</th>
                  <th>{t("team.inviteRole")}</th>
                  <th>
                    <span className="sr-only">{t("team.remove")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {team.members.map((m) => {
                  // A manager may change members and managers but not owners; an owner may change anyone.
                  const mayEdit = canManage && (isOwner || m.role !== "owner") && !m.you;
                  return (
                    <tr key={m.userId}>
                      <td>
                        {m.name || "—"} {m.you && <span className="hint">{t("team.you")}</span>}
                      </td>
                      <td>{m.email}</td>
                      <td>
                        {mayEdit ? (
                          <select aria-label={t("team.changeRole", { name: m.name || m.email })} value={m.role} onChange={(e) => void doRole(m.userId, e.target.value as Role)}>
                            {roleOptions.map((r) => (
                              <option key={r} value={r}>
                                {t(`role.${r}`)}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <Badge tone="muted">{t(`role.${m.role}`)}</Badge>
                        )}
                      </td>
                      <td>
                        {m.you ? (
                          <button className="btn ghost" onClick={() => void remove(m.userId, m.name, true)}>
                            {t("team.leave")}
                          </button>
                        ) : (
                          mayEdit && (
                            <button className="btn ghost" aria-label={`${t("team.remove")} ${m.name || m.email}`} onClick={() => void remove(m.userId, m.name || m.email, false)}>
                              {t("team.remove")}
                            </button>
                          )
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <h2>{t("team.invite")}</h2>
          {!canManage ? (
            <p className="hint">{t("team.cannotInvite")}</p>
          ) : !me.user.emailVerified ? (
            <p className="hint">{t("team.verifyFirst")}</p>
          ) : (
            <form className="card" onSubmit={send} noValidate>
              <TextInput label={t("team.inviteEmail")} type="email" autoComplete="off" value={email} onChange={setEmail} required />
              <Field label={t("team.inviteRole")} htmlFor="invite-role" help={t(`role.${role}.help`)}>
                <select id="invite-role" value={role} aria-describedby="invite-role-help" onChange={(e) => setRole(e.target.value as Role)}>
                  {roleOptions.map((r) => (
                    <option key={r} value={r}>
                      {t(`role.${r}`)}
                    </option>
                  ))}
                </select>
              </Field>
              <button className="btn primary" type="submit" disabled={busy || !email.trim()}>
                {busy ? t("team.sending") : t("team.send")}
              </button>
              {sent && <p role="status">{t("team.sent", { email: sent })}</p>}
            </form>
          )}

          <h2>{t("team.pending")}</h2>
          {team.invitations.length === 0 ? (
            <p className="hint">{t("team.pendingNone")}</p>
          ) : (
            <ul className="plain-list">
              {team.invitations.map((i) => (
                <li key={i.id} className="row between">
                  <span>
                    {i.email} · {t(`role.${i.role}`)} <span className="hint">({t("team.expires", { date: new Date(i.expiresAt).toLocaleDateString(lang) })})</span>
                  </span>
                  {canManage && (isOwner || i.role !== "owner") && (
                    <button className="btn ghost" onClick={() => void api.revokeInvite(i.id).then(load, fail)}>
                      {t("team.revoke")}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </main>
  );
}

export { errorText };
